import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { loginApi, getMeApi } from '../lib/api';

const AuthContext = createContext(null);

// Role hierarchy: admin > operator > viewer
const ROLES = ['viewer', 'operator', 'admin'];
const DEFAULT_ROLE = 'viewer';

// Permission definitions per role
const ROLE_PERMISSIONS = {
  admin: [
    'all',
    'view_admin_panel',
    'create_booking', 'edit_booking', 'release_booking',
    'approve_payment', 'reject_payment', 'view_payment_proof',
    'create_block', 'edit_block', 'delete_block',
    'create_recurring_block', 'edit_recurring_block', 'delete_recurring_block',
    'manage_promo_rules', 'manage_facility_rules',
    'manage_coaching_links', 'create_coaching_session', 'delete_coaching_session',
    'manage_open_play', 'manage_users',
    'view_audit_logs'
  ],
  operator: [
    'view_schedule',
    'create_booking', 'edit_booking', 'release_booking',
    'approve_payment', 'reject_payment', 'view_payment_proof',
    'manage_open_play', 'checkin_players'
  ],
  viewer: [
    'view_schedule'
  ]
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('pp_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await getMeApi();
      setUser(res.data);
    } catch {
      localStorage.removeItem('pp_token');
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await loginApi(email, password);
    localStorage.setItem('pp_token', res.data.token);
    // Normalize role from response
    const userData = res.data.user;
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('pp_token');
    setUser(null);
  };

  // Get normalized role (admin, operator, viewer)
  const getRole = () => {
    if (!user) return DEFAULT_ROLE;
    return user.role || DEFAULT_ROLE;
  };

  // Check if user has a specific permission
  const hasPermission = (permission) => {
    if (!user) return false;
    const role = getRole();
    const perms = ROLE_PERMISSIONS[role] || [];
    return perms.includes('all') || perms.includes(permission);
  };

  // Check if user has one of the allowed roles
  const hasRole = (...allowedRoles) => {
    if (!user) return false;
    const role = getRole();
    return allowedRoles.includes(role);
  };

  // Check if user is admin
  const isAdmin = () => hasRole('admin');

  // Check if user is operator or higher
  const isOperator = () => hasRole('admin', 'operator');

  // Check if user can access admin view
  const canAccessAdminView = () => isAdmin();

  // Check if user can approve/reject payments
  const canApprovePayments = () => isOperator();

  // Check if user can create blocks/schedules
  const canManageSchedule = () => isAdmin();

  // Check if user can manage coaching links
  const canManageCoaching = () => isAdmin();

  // Check if user can manage facility rules/promo
  const canManageSettings = () => isAdmin();

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      logout, 
      hasPermission,
      hasRole,
      getRole,
      isAdmin,
      isOperator,
      canAccessAdminView,
      canApprovePayments,
      canManageSchedule,
      canManageCoaching,
      canManageSettings
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
